import React, { useState, useEffect } from 'react'
import ApiUsers from '../../api/ApiUsers'
import ApiPlayers from '../../api/ApiPlayers'
import toastr from 'toastr'
import '../auth/InlineProfileCompletionModal.css'

function computeInitialFormData(show, primaryPlayer, user) {
  const safeShow = Boolean(show && primaryPlayer && user)
  return {
    email: safeShow ? user.email || '' : '',
    phone_number: safeShow ? primaryPlayer.phone_number || '' : '',
  }
}

export default function ContactInfoModal({
  show,
  onComplete,
  onDismiss,
  primaryPlayer,
  user,
}) {
  const [formData, setFormData] = useState(() =>
    computeInitialFormData(show, primaryPlayer, user),
  )
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})

  // Initialize form data when modal opens
  useEffect(() => {
    if (show && primaryPlayer && user) {
      setFormData(computeInitialFormData(true, primaryPlayer, user))
    }
  }, [show, primaryPlayer, user])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    if (!formData.phone_number.trim()) {
      newErrors.phone_number = 'Phone number is required'
    } else if (
      !/^\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/.test(
        formData.phone_number.replace(/\s+/g, ''),
      )
    ) {
      // Basic US phone validation - allows various formats
      newErrors.phone_number = 'Please enter a valid 10-digit phone number'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return

    setSaving(true)
    try {
      // Update user data (email)
      await ApiUsers.update({
        ...user,
        email: formData.email,
      })

      // Update player data (phone_number)
      await ApiPlayers.update({
        id: primaryPlayer.id,
        phone_number: formData.phone_number,
      })

      toastr.success('Contact information saved successfully!')
      onComplete()
    } catch (error) {
      console.error('Error saving contact info:', error)
      toastr.error(
        'Error saving contact information: ' +
          (error.message || 'Please try again'),
      )
    } finally {
      setSaving(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !saving) {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape' && onDismiss) {
      e.preventDefault()
      onDismiss()
    }
  }

  const handleOverlayClick = (e) => {
    if (e.target.className === 'fl-sheet-overlay' && onDismiss) {
      onDismiss()
    }
  }

  if (!show) return null

  return (
    <div
      className="fl-sheet-overlay"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-info-title"
      onKeyDown={handleKeyDown}
      onClick={handleOverlayClick}
    >
      <div className="fl-sheet" role="document">
        <div className="fl-sheet-header">
          <h2 id="contact-info-title" className="fl-sheet-title">
            📱 Contact Information Required
          </h2>
          <p className="fl-sheet-subtitle">
            We need your contact info so opponents can reach you if needed.
          </p>
        </div>

        <div className="fl-sheet-body">
          <form className="fl-sections">
            <div className="fl-field">
              <label htmlFor="email" className="fl-label">
                Email Address <span className="fl-required">*</span>
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className={`fl-input ${errors.email ? 'fl-invalid' : ''}`}
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your.email@example.com"
              />
              {errors.email && <div className="fl-error">{errors.email}</div>}
            </div>

            <div className="fl-field">
              <label htmlFor="phone_number" className="fl-label">
                Phone Number <span className="fl-required">*</span>
              </label>
              <input
                id="phone_number"
                name="phone_number"
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                className={`fl-input ${errors.phone_number ? 'fl-invalid' : ''}`}
                value={formData.phone_number}
                onChange={handleInputChange}
                placeholder="(555) 123-4567"
              />
              {errors.phone_number && (
                <div className="fl-error">{errors.phone_number}</div>
              )}
            </div>
          </form>
        </div>

        <div className="fl-actions">
          <button
            type="button"
            className="fl-btn fl-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save & Continue'}
          </button>
        </div>
      </div>
    </div>
  )
}
